/* eslint-disable no-console */
import React, { useContext, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router';
import { getPet, getPetWithOwnerName, getActivePets } from 'api/petApi';
import FetchError from 'types/http';
import { PetInterface } from 'types/pet';
import { Helmet } from 'react-helmet-async';
import { KibDownloadIcon } from '@chewy/kib-icons-react';
import KibDivider from '@chewy/kib-divider-react';
import classNames from 'classnames';
import { KibSheet } from '@chewy/kib-overlays-react';
import MemorialHeader from 'components/pet-memorial/MemorialHeader';
import MemorialMenu from 'components/pet-memorial/MemorialMenu';
import { KibButtonNew } from '@chewy/kib-controls-react';
import MemorialContent from 'components/pet-memorial/MemorialContent';
import MemorialContentFooter from 'components/pet-memorial/MemorialContentFooter';
import MemorialDetails from 'components/pet-memorial/MemorialDetails';
import AnalyticsContext from '../context/AnalyticsContext';
import { retryCount } from '../util/reactQueryOptions';
import {
  ApiPetProfileAvatarImageSourceEnum,
  ApiPetProfileStatusEnum,
  ApiPetProfileStatusReasonEnum,
} from '../api/generated-client/PetProfile-Service-A.v1';
import MemorialRemoved from '../components/pet-memorial/MemorialRemoved';
import PageLoader from '../components/loaders/PageLoader';
import { ACCOUNT_OVERVIEW_PATH, getPetProfilePath } from '../util/config';
import cookie from '../util/cookie';
import './memorial-page.scss';

export default function MemorialPage() {
  const { petId } = useParams<{ petId: string }>();

  const petProfileQuery = useQuery<PetInterface, FetchError>([getPet.key, petId], getPet, {
    retry: retryCount,
  });

  const petProfile = petProfileQuery.data;
  const petProfileStatus = petProfileQuery.status;
  const queryClient = useQueryClient();

  const [inactivePet, setInactivePet] = useState(false);
  const [showDownloadSheet, setShowDownloadSheet] = useState(false);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string>('');
  const [avatarUrl, setAvatarUrl] = useState(petProfile?.avatarUrl);
  const [avatarSrc, setAvatarSrc] = useState<ApiPetProfileAvatarImageSourceEnum | undefined>(
    petProfile?.avatarSource,
  );

  const [openDetails, setOpenDetails] = useState(false);
  const [removedMemorial, setRemovedMemorial] = useState(false);

  const memorialPageClasses = classNames('memorial-page', {
    'memorial-page--main': !openDetails,
    'memorial-page--details': openDetails,
  });

  const analytics = useContext(AnalyticsContext);

  useEffect(() => {
    // remove cookie if exists
    cookie.delete('memorialized');
    analytics.trackRainbow({
      name: 'pet profile memorial - viewed',
      category: 'pet-profile-memorial',
      action: 'impression',
      label: 'pet_profile_memorial_impression',
    });

    // Only redirect if the query is done loading and petProfile is still undefined
    if (petProfileQuery.status !== 'loading' && !petProfile) {
      console.log('[MemorialPage] petProfile is undefined. Redirecting.');
      window.location.href = ACCOUNT_OVERVIEW_PATH;
    } else {
      console.log('[MemorialPage] petProfile is available or still loading. No account redirect.');
    }
  }, [petProfile, petProfileQuery.status, analytics]);

  const handleDownloadClick = async () => {
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_photo_download',
    });
  };

  const handleViewDownloadSheet = () => {
    analytics.trackRainbow({
      name: 'pet profile memorial - viewed',
      category: 'pet-profile-memorial',
      action: 'impression',
      label: 'pet_profile_memorial_photo_impression',
    });
  };

  const handleCloseDownloadSheet = () => {
    setShowDownloadSheet(false);
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_photo_close',
    });
  };

  const hasPassed = petProfile?.statusReason === ApiPetProfileStatusReasonEnum.Deceased;

  const invalidateQueries = () => {
    const invalidQueries = [
      queryClient.removeQueries(getActivePets.key),
      queryClient.removeQueries([getPet.key, petId]),
      queryClient.removeQueries([getPetWithOwnerName.key, petId]),
    ];
    return Promise.all(invalidQueries);
  };

  useEffect(() => {
    invalidateQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const activePet = petProfile?.status === ApiPetProfileStatusEnum.Active;
    if (activePet) {
      window.location.href = getPetProfilePath(petId);
    } else setInactivePet(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petId]);

  if (petProfile && !!inactivePet) {
    return (
      <section className="memorial-page-section">
        <Helmet>
          <title>{petProfile.name}'s Memorial | Chewy.com</title>
        </Helmet>
        {removedMemorial ? (
          <div>
            <MemorialRemoved petProfile={petProfile} />
          </div>
        ) : (
          <div className={memorialPageClasses} data-testid="memorial-page">
            <MemorialMenu
              petProfile={petProfile}
              openDetails={openDetails}
              setOpenDetails={setOpenDetails}
              setRemovedMemorial={setRemovedMemorial}
            />
            <div className="memorial-content-container" data-testid="memorial-content-container">
              {!!openDetails ? (
                <div>
                  <MemorialDetails petProfile={petProfile} />
                </div>
              ) : (
                <>
                  <MemorialHeader
                    petProfile={petProfile}
                    avatarUrl={avatarUrl}
                    avatarSrc={avatarSrc}
                    hasPassed={hasPassed}
                  />
                  <MemorialContent
                    petProfile={petProfile}
                    avatarUrl={avatarUrl}
                    setAvatarUrl={setAvatarUrl}
                    avatarSrc={avatarSrc}
                    setAvatarSrc={setAvatarSrc}
                    setShowDownloadSheet={setShowDownloadSheet}
                    setSelectedPhotoUrl={setSelectedPhotoUrl}
                    setOpenDetails={setOpenDetails}
                  />
                  {!!hasPassed ? (
                    <MemorialContentFooter />
                  ) : (
                    <div className="memorial-page__filler-space" />
                  )}
                  <KibSheet
                    open={showDownloadSheet}
                    onOpen={handleViewDownloadSheet}
                    onClose={handleCloseDownloadSheet}
                    bottom
                    title=""
                    size="large"
                    data-testid="memorial-download-sheet"
                    className="memorial-download-sheet"
                  >
                    <img
                      src={selectedPhotoUrl}
                      alt="banner"
                      className="memorial-download-sheet__photo"
                    />
                    <KibDivider className="memorial-download-sheet__divider" />
                    <KibButtonNew
                      data-testid="download-photo-button"
                      aria-label="download-photo"
                      emphasis="primary"
                      size="large"
                      href={`${selectedPhotoUrl}?download=true`}
                      className="memorial-download-sheet__button"
                      onClick={handleDownloadClick}
                      target="_blank"
                    >
                      <KibDownloadIcon
                        data-testid="pet-profile-planner-big-event-icon-chevron"
                        aria-hidden="true"
                        className="memorial-download-sheet__icon"
                      />
                      Download Photo
                    </KibButtonNew>
                  </KibSheet>
                </>
              )}
            </div>
          </div>
        )}
      </section>
    );
  }

  if (!inactivePet || petProfileStatus === 'idle' || petProfileStatus === 'loading') {
    return (
      <div
        className="kib-margin__top"
        style={{ display: 'flex' }}
        data-testid="memorial-page-loader"
      >
        <PageLoader />
      </div>
    );
  }
}
